import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import Cropper, { Area } from "react-easy-crop";
import styled from "styled-components";

import Modal from "../modal/Modal";
import SwitchComponent from "../form-item/SwitchComponent";
import {
  getCroppedImg,
  resizeImage,
  base64ToFile,
  CropData,
  CropResult,
} from "./cropUtils";
import {
  allowsCustomAspect,
  allowsOriginalAspect,
  aspectToSliderPosition,
  canUseOriginalAspect,
  clampAspect,
  formatAspect,
  getAspectPresets,
  getInitialAspectSelection,
  isSameAspect,
  resolveSelectionForImage,
  sliderPositionToAspect,
  type AspectRule,
  type AspectSelection,
} from "./aspectRule";

// ===== Types =====

export interface ImageEditorModalProps {
  /** 元画像の URL または base64 */
  imageSrc: string;
  /** 比率の制約（例: ASPECT_RULES.projectBanner） */
  aspectRule: AspectRule;
  /** クロップ形状 */
  cropShape?: "rect" | "round";
  /** 出力幅（aspectRule が fixed のときのみ。outputHeight と組で指定） */
  outputWidth?: number;
  /** 出力高さ（aspectRule が fixed のときのみ。outputWidth と組で指定） */
  outputHeight?: number;
  /** 出力の長辺の上限 px（比率を保って縮小する） */
  maxOutputEdge?: number;
  /** 出力フォーマット */
  outputFormat?: "image/jpeg" | "image/png" | "image/webp";
  /** 出力ファイル名 */
  outputFileName?: string;
  /** 前回の切り抜き座標（再編集時に位置・ズーム・比率を復元する） */
  initialCropData?: CropData | null;
  /** true の場合、ローディング表示しボタンを無効化 */
  loading?: boolean;
  /** 確定時コールバック（Promise を返すと完了までローディング表示） */
  // eslint-disable-next-line no-unused-vars
  onConfirm: (result: CropResult) => void | Promise<void>;
  /** キャンセル時コールバック */
  onCancel: () => void;
  /** 画像を選び直すコールバック（指定時に「画像を変更」ボタンを表示） */
  onReselect?: () => void;
  /** 追加UIスロット（AI画像生成ボタン等） */
  renderExtra?: () => React.ReactNode;
}

interface ImageSize {
  width: number;
  height: number;
}

type SwitchKey = string;
const CUSTOM_KEY = "custom";
const ORIGINAL_KEY = "original";
const presetKey = (index: number) => `preset:${index}`;

const SLIDER_STEPS = 1000;
const MAX_ZOOM = 8;
/** 前回の座標を当てるときの縦横比の許容差（ピクセルの丸めを吸収する） */
const RESTORE_ASPECT_TOLERANCE = 0.02;

// ===== Styled Components =====

// 枠の高さは比率に関係なく固定し、切り抜き範囲の描画は react-easy-crop に任せる
// （aspect-ratio で枠を作ると、縦長や極端な比率で高さの上限に当たって崩れる）。
const CropperContainer = styled.div`
  width: 100%;
  height: min(60dvh, 400px);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  background: var(--soremo-background, #f0f0f0);
`;

// 「そのまま」選択時のプレビュー（クロップなし）
const PlainPreview = styled.div`
  width: 100%;
  height: min(60dvh, 400px);
  border-radius: 4px;
  overflow: hidden;
  background: var(--soremo-background, #f0f0f0);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const RangeSlider = styled.input`
  width: 100%;
  accent-color: var(--soremo-blue);
`;

const SliderLabel = styled.span.attrs({ className: "bodytext-11" })`
  flex: 0 0 auto;
  min-width: 64px;
  color: var(--soremo-gray);
  white-space: nowrap;
`;

/** 前回の座標が今の画像に収まっているか（別画像の座標を当てない） */
const isCropInside = (crop: CropData, size: ImageSize): boolean =>
  crop.width > 0 &&
  crop.height > 0 &&
  crop.x >= 0 &&
  crop.y >= 0 &&
  crop.x + crop.width <= size.width + 1 &&
  crop.y + crop.height <= size.height + 1;

// ===== Component =====

const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  imageSrc,
  aspectRule,
  cropShape = "rect",
  outputWidth,
  outputHeight,
  maxOutputEdge,
  outputFormat = "image/jpeg",
  outputFileName = "cropped-image.jpg",
  initialCropData,
  loading: externalLoading = false,
  onConfirm,
  onCancel,
  onReselect,
  renderExtra,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [imageSize, setImageSize] = useState<ImageSize | null>(null);
  const [selection, setSelection] = useState<AspectSelection>(() =>
    getInitialAspectSelection(aspectRule, initialCropData),
  );
  // 前回の座標を当てるのは、最初に開いた画像を比率を変えずに編集するときだけ
  const [initialSrc] = useState(imageSrc);
  const [keepInitialCrop, setKeepInitialCrop] = useState(true);

  const isLoading = externalLoading || internalLoading;
  const imageAspect = imageSize ? imageSize.width / imageSize.height : null;
  // 「そのまま」はクロップせず元画像全体を使う
  const isPassthrough = selection.mode === "original";
  const activeAspect = selection.mode === "original" ? null : selection.aspect;

  // 呼び出し側がインラインのオブジェクトで渡しても画像を読み直さないよう ref で持つ
  const aspectRuleRef = useRef(aspectRule);
  aspectRuleRef.current = aspectRule;

  // 元画像のサイズを取る（「そのまま」の可否・座標の復元・passthrough 出力に使う）
  useEffect(() => {
    let cancelled = false;
    setImageSize(null);
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      const size = { width: image.naturalWidth, height: image.naturalHeight };
      setImageSize(size);
      setSelection((prev) =>
        resolveSelectionForImage(prev, aspectRuleRef.current, size.width / size.height),
      );
    };
    image.src = imageSrc;
    return () => {
      cancelled = true;
    };
  }, [imageSrc]);

  // 画像を選び直したら位置・ズームを初期化する
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, [imageSrc]);

  const restoredCropArea = useMemo(() => {
    if (!keepInitialCrop || imageSrc !== initialSrc) return undefined;
    if (!initialCropData || !imageSize || !isCropInside(initialCropData, imageSize)) {
      return undefined;
    }
    const { x, y, width, height } = initialCropData;
    // 制約が変わって比率が合わない座標（例: 5:1 で切った古いデータを縦カードで開く）は当てない
    if (
      activeAspect === null ||
      !isSameAspect(width / height, activeAspect, RESTORE_ASPECT_TOLERANCE)
    ) {
      return undefined;
    }
    return { x, y, width, height };
  }, [keepInitialCrop, imageSrc, initialSrc, initialCropData, imageSize, activeAspect]);

  const presets = useMemo(
    () => (aspectRule.type === "fixed" ? [] : getAspectPresets(aspectRule)),
    [aspectRule],
  );

  const switchOptions = useMemo(() => {
    const options: { label: string; value: SwitchKey }[] = presets.map((preset, index) => ({
      label: formatAspect(preset),
      value: presetKey(index),
    }));
    if (allowsCustomAspect(aspectRule)) {
      options.push({ label: "カスタム", value: CUSTOM_KEY });
    }
    if (allowsOriginalAspect(aspectRule) && canUseOriginalAspect(aspectRule, imageAspect)) {
      options.push({ label: "そのまま", value: ORIGINAL_KEY });
    }
    return options;
  }, [presets, aspectRule, imageAspect]);

  const switchValue = useMemo<SwitchKey>(() => {
    if (selection.mode === "original") return ORIGINAL_KEY;
    if (selection.mode === "custom") return CUSTOM_KEY;
    const index = presets.findIndex((preset) => isSameAspect(preset, selection.aspect));
    return index >= 0 ? presetKey(index) : CUSTOM_KEY;
  }, [selection, presets]);

  // 比率を変えたら位置とズームを初期化する。切り抜き範囲は Cropper が比率の変更や
  // 再マウントで通知し直すので、ここでは消さない（同じ比率だと通知が来ないため）。
  const resetCropArea = useCallback(() => {
    setKeepInitialCrop(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const handleSwitchChange = useCallback(
    (key: SwitchKey | null) => {
      if (key === null || key === switchValue) return;
      if (key === ORIGINAL_KEY) {
        setSelection({ mode: "original" });
      } else if (key === CUSTOM_KEY) {
        // 今の比率のままスライダーで調整を始めるので、位置も保つ
        setKeepInitialCrop(false);
        setSelection({
          mode: "custom",
          aspect: clampAspect(activeAspect ?? imageAspect ?? 1, aspectRule),
        });
        return;
      } else {
        const preset = presets[Number(key.split(":")[1])];
        if (preset === undefined) return;
        setSelection({ mode: "preset", aspect: preset });
      }
      resetCropArea();
    },
    [switchValue, activeAspect, imageAspect, aspectRule, presets, resetCropArea],
  );

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setKeepInitialCrop(false);
      setSelection({
        mode: "custom",
        aspect: sliderPositionToAspect(Number(e.target.value) / SLIDER_STEPS, aspectRule),
      });
    },
    [aspectRule],
  );

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixelsVal: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsVal);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (isLoading) return;

    try {
      setInternalLoading(true);

      let cropData: CropData;
      if (isPassthrough) {
        if (!imageSize) return;
        cropData = { x: 0, y: 0, width: imageSize.width, height: imageSize.height, aspect: null };
      } else {
        if (!croppedAreaPixels) return;
        cropData = { ...croppedAreaPixels, aspect: activeAspect };
      }

      let base64 = await getCroppedImg(imageSrc, cropData, outputFormat);

      if (aspectRule.type === "fixed" && outputWidth && outputHeight) {
        base64 = await resizeImage(base64, outputWidth, outputHeight, outputFormat);
      } else if (maxOutputEdge) {
        const longEdge = Math.max(cropData.width, cropData.height);
        if (longEdge > maxOutputEdge) {
          const scale = maxOutputEdge / longEdge;
          base64 = await resizeImage(
            base64,
            Math.round(cropData.width * scale),
            Math.round(cropData.height * scale),
            outputFormat,
          );
        }
      }

      const file = base64ToFile(base64, outputFileName, outputFormat);

      await onConfirm({
        file,
        base64,
        cropData,
      });
    } catch (error) {
      console.error("Image crop failed:", error);
    } finally {
      setInternalLoading(false);
    }
  }, [
    isLoading,
    isPassthrough,
    imageSize,
    croppedAreaPixels,
    activeAspect,
    imageSrc,
    outputFormat,
    aspectRule.type,
    outputWidth,
    outputHeight,
    maxOutputEdge,
    outputFileName,
    onConfirm,
  ]);

  const handleReselectClick = useCallback(() => {
    onReselect?.();
  }, [onReselect]);

  const canConfirm = isPassthrough ? Boolean(imageSize) : Boolean(croppedAreaPixels);

  return (
    <Modal
      size="default"
      onClose={isLoading ? undefined : onCancel}
      primaryAction={{
        label: "更新",
        onClick: handleConfirm,
        disabled: !canConfirm,
        loading: isLoading,
        rightIcon: "chevron_right",
      }}
      secondaryAction={
        onReselect
          ? {
              label: "画像を変更",
              onClick: handleReselectClick,
              disabled: isLoading,
              theme: "secondary",
              variant: "outlined",
              leftIcon: "upload",
            }
          : undefined
      }
    >
      {/* margin-top は CloseButton (top: 8px + height: 32px = 下端 40px) を回避するため */}
      <div className="u-col u-gap8 u-w100 u-mt16">
        {switchOptions.length > 1 && (
          <SwitchComponent<SwitchKey>
            value={switchValue}
            options={switchOptions}
            onChange={handleSwitchChange}
          />
        )}

        {selection.mode === "custom" && (
          <div className="u-row u-gap8 u-w100 u-align-center">
            <SliderLabel>比率 {formatAspect(selection.aspect)}</SliderLabel>
            <RangeSlider
              type="range"
              min={0}
              max={SLIDER_STEPS}
              step={1}
              value={Math.round(aspectToSliderPosition(selection.aspect, aspectRule) * SLIDER_STEPS)}
              onChange={handleSliderChange}
              disabled={isLoading}
              aria-label="切り抜きの比率"
            />
          </div>
        )}

        {isPassthrough ? (
          <PlainPreview>
            <img src={imageSrc} alt="元画像プレビュー" />
          </PlainPreview>
        ) : (
          <>
            <CropperContainer>
              {imageSize && activeAspect !== null && (
                <Cropper
                  key={imageSrc}
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  maxZoom={MAX_ZOOM}
                  aspect={activeAspect}
                  cropShape={cropShape}
                  showGrid={cropShape === "rect"}
                  initialCroppedAreaPixels={restoredCropArea}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              )}
            </CropperContainer>

            <RangeSlider
              type="range"
              min={1}
              max={MAX_ZOOM}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={isLoading}
              aria-label="ズーム"
            />
          </>
        )}

        {renderExtra?.()}
      </div>
    </Modal>
  );
};

export default ImageEditorModal;
